// Library imports
import React, { useState } from "react";

// User Component imports
import OpsDialog from "../common/OpsDialog";
import { PROMPT_GROUPS, PROMPT_TABS } from "../utils/promptVersions";
import { IS_MAC, shortcutLabel } from "../utils/shortcut";
import { VARIABLE_INFO, assistVariablesOf, autoSentVariablesOf } from "../utils/promptVariables";
import { AutoSentList } from "./AutoSentInfo";

const TABS = [
    { key: "flow", label: "はじめに", icon: "flag" },
    { key: "screen", label: "画面の見方", icon: "layout-sidebar" },
    { key: "rules", label: "書き方のルール", icon: "braces" },
    { key: "keys", label: "ショートカット", icon: "keyboard" },
];

const STEPS = [
    { icon: "pencil-square", title: "編集", text: "右のエディタで書き換える。途中の内容はブラウザに自動で残ります" },
    { icon: "save", title: "保存", text: "差分を見ながらメモを付けて、新しいバージョンとして残す" },
    { icon: "check2-circle", title: "適用", text: "稼働中のAIにすぐ反映。いつでも前の版に戻せます" },
];

const SHORTCUTS = [
    { keys: [shortcutLabel("S")], text: "バージョンを保存" },
    { keys: [shortcutLabel("B")], text: "太字" },
    { keys: [shortcutLabel("K")], text: "リンクを挿入" },
    { keys: [shortcutLabel("Z")], text: "元に戻す" },
    { keys: [IS_MAC ? "⇧⌘Z" : shortcutLabel("Y")], text: "やり直す" },
    { keys: ["{"], text: "変数の候補を表示（↑↓で選択、Enter / Tab で確定、Esc で閉じる）" },
    { keys: ["Tab"], text: "インデントを付ける" },
    { keys: ["Shift", "Tab"], text: "インデントを戻す" },
];

const LEGEND = [
    { visual: <span className="ops-help-band" />, title: "色の帯", text: "いまエディタに表示している版" },
    { visual: <span className="ops-ver-tag is-deployed">適用中</span>, title: "適用中", text: "稼働中のAIが使っている版" },
    { visual: <span className="ops-ver-tag is-draft">未保存</span>, title: "編集中", text: "まだ保存していない編集" },
    { visual: <i className="bi bi-star-fill ops-help-star" />, title: "スター", text: "良かった版の目印（運営メンバーで共有）" },
    { visual: <i className="bi bi-pencil ops-help-pencil" />, title: "メモ編集", text: `鉛筆かメモのダブルクリックで、保存した版の変更メモを書き換える（Enter で改行、${shortcutLabel("Enter")} で保存、Esc で取り消し）` },
    { visual: <span className="ops-help-chip"><i className="bi bi-pencil-square" />読み込み</span>, title: "読み込み", text: "その版をエディタに展開する" },
    { visual: <span className="ops-help-chip"><i className="bi bi-file-diff" />差分</span>, title: "差分", text: "適用中の内容（サーバーの内容）と比べる" },
    { visual: <span className="ops-help-chip"><i className="bi bi-arrow-counterclockwise" />編集破棄</span>, title: "編集破棄", text: "システム／ユーザーのプロンプトごとに、編集をやめて適用中の内容に戻す" },
];

// ○ / ✕ の書き方の見本
const Example = ({ good, children }) => (
    <div className={"ops-help-example" + (good ? " is-good" : " is-bad")}>
        <i className={"bi " + (good ? "bi-check-circle-fill" : "bi-x-circle-fill")} />
        <code>{children}</code>
    </div>
);

const FlowTab = () => (
    <>
        <div className="ops-help-steps">
            {STEPS.map((step, i) => (
                <React.Fragment key={step.title}>
                    {i > 0 && <i className="bi bi-chevron-right ops-help-step-arrow" />}
                    <div className="ops-help-step">
                        <span className="ops-help-step-num">{i + 1}</span>
                        <div className="ops-help-step-icon"><i className={"bi bi-" + step.icon} /></div>
                        <div className="ops-help-step-title">{step.title}</div>
                        <div className="ops-help-step-text">{step.text}</div>
                    </div>
                </React.Fragment>
            ))}
        </div>
        <div className="ops-help-callout is-warn">
            <i className="bi bi-exclamation-triangle-fill" />
            <div><b>適用すると直ぐに反映されます。</b></div>
        </div>
    </>
);

const ScreenTab = () => (
    <div className="ops-help-legend">
        {LEGEND.map((it) => (
            <div key={it.title} className="ops-help-legend-item">
                <div className="ops-help-legend-visual">{it.visual}</div>
                <div><b>{it.title}</b>　{it.text}</div>
            </div>
        ))}
    </div>
);

// プロンプト（グループ×タブ）ごとの使える変数
// システムが自動で送る変数は書くと重複するため案内しない
const VarsRow = ({ label, groupKey, templateVars }) => {
    const vars = assistVariablesOf(groupKey, templateVars);
    return (
        <div className="ops-help-vars-row">
            <span className="ops-help-vars-tab">{label}</span>
            {vars.length ? vars.map((v) => <code key={v} title={VARIABLE_INFO[v]?.label}>{"{" + v + "}"}</code>)
                : <span className="ops-help-vars-none">{templateVars ? "必要な情報はシステムが自動で送ります（下の「自動で送る情報」を参照。変数は書かないでください）" : "変数なし（{ } はそのまま文字になります）"}</span>}
        </div>
    );
};

const RulesTab = ({ groups, groupKey }) => {
    const [showAll, setShowAll] = useState(false);
    const varsOf = (gk, tab) => groups[gk]?.rules?.[tab]?.template_variables;
    const current = PROMPT_GROUPS.find((g) => g.key === groupKey);
    return (
        <>
            <div className="ops-help-callout is-warn">
                <i className="bi bi-exclamation-triangle-fill" />
                <div>
                    <b>書き方に誤りがあると、エディタで指摘されます。</b>
                    <div className="ops-help-severity">
                        <span className="ops-help-sev-item"><span className="ops-help-sev is-error"><i className="bi bi-x-octagon-fill" />エラー</span>保存・適用できません</span>
                        <span className="ops-help-sev-item"><span className="ops-help-sev is-warn"><i className="bi bi-exclamation-triangle-fill" />警告</span>保存できますが、誤りがある可能性があります（見直し推奨）</span>
                    </div>
                </div>
            </div>
            <div className="ops-help-cards">
                <div className="ops-help-card is-wide">
                    <div className="ops-help-card-title"><i className="bi bi-exclamation-circle" />{"{ }"} はなるべく使わない</div>
                    <div className="ops-help-card-text">{"{ }"} は変数として扱われるため、本文ではなるべく使わないでください。どうしても文字として書く場合は2つ重ねます。</div>
                    <div className="ops-help-examples">
                        <Example good={false}>{"{name}"}</Example>
                        <i className="bi bi-arrow-right ops-help-example-arrow" />
                        <Example good={true}>{"{{name}}"}</Example>
                    </div>
                    <div className="ops-help-vars">
                        <span className="ops-help-vars-label">{current?.name} で使える変数</span>
                        {PROMPT_TABS.map((t) => <VarsRow key={t.key} label={t.label} groupKey={groupKey} templateVars={varsOf(groupKey, t.key)} />)}
                        <button type="button" className="ops-help-more" onClick={() => setShowAll((v) => !v)}>
                            <i className={"bi " + (showAll ? "bi-chevron-up" : "bi-chevron-down")} />{showAll ? "閉じる" : "ほかのプロンプトの変数も見る"}
                        </button>
                        {showAll && PROMPT_GROUPS.filter((g) => g.key !== groupKey).map((g) => PROMPT_TABS.map((t) => (
                            <VarsRow key={g.key + t.key} label={`${g.name}／${t.label}`} groupKey={g.key} templateVars={varsOf(g.key, t.key)} />
                        )))}
                    </div>
                </div>

                <div className="ops-help-card">
                    <div className="ops-help-card-title"><i className="bi bi-link-45deg" />リンク</div>
                    <div className="ops-help-card-text">この書き方でウィジェットにリンクが表示されます。</div>
                    <Example good={true}>{"[公式サイト](https://…)"}</Example>
                    <Example good={true}>{"[担当者に相談](inquiry://open)"}</Example>
                </div>

                <div className="ops-help-card">
                    <div className="ops-help-card-title"><i className="bi bi-image" />画像タグ</div>
                    <div className="ops-help-card-text">最終返答のユーザープロンプトで使えます。</div>
                    <Example good={true}>{"@@img(https://…)"}</Example>
                    <Example good={false}>{"@@img(http://…)"}</Example>
                    <Example good={false}>{"@@img（https://…）"}</Example>
                </div>

                <div className="ops-help-card is-wide">
                    <div className="ops-help-card-title"><i className="bi bi-send" />自動で送る情報（最終返答）</div>
                    <div className="ops-help-card-text">最終返答のシステムプロンプトの後ろに、システムが次の情報を自動で付けて送ります。プロンプトに書かなくても AI に届きます。</div>
                    <AutoSentList items={autoSentVariablesOf("character")} />
                </div>
            </div>
        </>
    );
};

const KeysTab = () => (
    <div className="ops-help-keys">
        <div className="ops-help-lead">プロンプト編集欄で使えるショートカット</div>
        {SHORTCUTS.map((s) => (
            <div key={s.text} className="ops-help-key-row">
                <div className="ops-help-key-caps">
                    {s.keys.map((k, i) => (
                        <React.Fragment key={k}>
                            {i > 0 && <span className="ops-help-key-plus">+</span>}
                            <kbd>{k}</kbd>
                        </React.Fragment>
                    ))}
                </div>
                <div className="ops-help-key-text">{s.text}</div>
            </div>
        ))}
    </div>
);

/**
 * プロンプト管理の使い方（閲覧のみ。フッターが無いため×で閉じ、背景クリックでも閉じる）
 */
const PromptHelpModal = ({ groups, groupKey, onClose }) => {
    const [tab, setTab] = useState(TABS[0].key);
    return (
        <OpsDialog show={true} className="ops-scope ops-help-dialog" title="プロンプト管理の使い方" width={900} showClose={true} onClose={onClose}>
            <div className="ops-help">
                <div className="ops-help-tabs" role="tablist">
                    {TABS.map((t) => (
                        <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
                            className={"ops-help-tab" + (tab === t.key ? " is-active" : "")} onClick={() => setTab(t.key)}>
                            <i className={"bi bi-" + t.icon} /><span>{t.label}</span>
                        </button>
                    ))}
                </div>
                <div className="ops-help-panel">
                    {tab === "flow" && <FlowTab />}
                    {tab === "screen" && <ScreenTab />}
                    {tab === "rules" && <RulesTab groups={groups} groupKey={groupKey} />}
                    {tab === "keys" && <KeysTab />}
                </div>
            </div>
        </OpsDialog>
    );
};

export default PromptHelpModal;
